import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowRight as ArrowRightIcon, FolderKanban, FileText,
  ArrowRight, Clock, Plus, MessageSquare,
  Loader2, Shield, BarChart3, TrendingUp,
  CheckCircle2, MoreHorizontal, Paperclip, Mic, MicOff, X,
  Briefcase, Search, Workflow, BookOpen, FlaskConical,
  Globe, Zap, GraduationCap, Lightbulb, ChevronRight,
} from "lucide-react";
import { useSpeechToText } from "@/hooks/useAudio";
import { CreateProjectWizard } from "@/components/CreateProjectWizard";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

/* ─── 3-Dot Menu Options ─── */
const MENU_ITEMS = [
  { icon: Plus, label: "Create a Project", prompt: "Create a new project for ", workflowHint: "" },
  { icon: Search, label: "Do Market Research", prompt: "Research the market for ", workflowHint: "content" },
  { icon: Workflow, label: "Build a Workflow", prompt: "Build an AI workflow to ", workflowHint: "content" },
  { icon: Globe, label: "Localize Content", prompt: "Localize this content for ", workflowHint: "localization" },
  { icon: Briefcase, label: "Compliance Check", prompt: "Run a compliance check on ", workflowHint: "accessibility" },
];

/* ─── Metric Card ─── */
const MetricCard = ({ icon: Icon, label, value, trend, trendColor, color }) => (
  <div className="p-5 bg-white border border-border/60 rounded-2xl shadow-soft" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
      </div>
      {trend && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${trendColor}`}>{trend}</span>}
    </div>
    <p className="text-2xl font-heading font-bold text-foreground tracking-tight">{value}</p>
    <p className="text-[11px] text-foreground/40 uppercase tracking-wider font-semibold mt-0.5">{label}</p>
  </div>
);

/* ─── Progress Row ─── */
const ProgressRow = ({ label, pct, color }) => (
  <div className="flex items-center gap-3 py-2">
    <span className="text-xs text-foreground/60 w-36 shrink-0">{label}</span>
    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
    </div>
    <span className="text-xs font-semibold w-10 text-right" style={{ color }}>{pct}%</span>
  </div>
);

/* ─── Pipeline Row ─── */
const PipelineRow = ({ name, badge, badgeColor, pct, agents }) => (
  <div className="px-5 py-3 border-b border-border/30 last:border-0">
    <div className="flex items-center gap-2 mb-1.5">
      <div className={`w-2 h-2 rounded-full ${badge === "completed" ? "bg-cobalt" : badge === "running" ? "bg-emerald-500" : "bg-amber-400"}`} />
      <span className="text-sm text-foreground font-medium flex-1 truncate">{name}</span>
      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badgeColor}`}>{badge}</span>
      <span className="text-[10px] text-foreground/40 flex items-center gap-1"><Zap className="w-3 h-3" /> {agents} agents</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${badge === "completed" ? "bg-cobalt" : badge === "running" ? "bg-emerald-500" : "bg-gray-200"}`} style={{ width: `${pct}%` }} />
      </div>
      {pct > 0 && <span className="text-[10px] font-semibold text-foreground/50">{pct}%</span>}
    </div>
  </div>
);

/* ─── Activity Row ─── */
const ActivityRow = ({ dot, text, time }) => (
  <div className="flex items-start gap-3 px-5 py-3 border-b border-border/30 last:border-0">
    <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: dot }} />
    <div className="min-w-0 flex-1">
      <p className="text-xs text-foreground/70 leading-relaxed" dangerouslySetInnerHTML={{ __html: text }} />
      <p className="text-[10px] text-foreground/50 mt-0.5">{time}</p>
    </div>
  </div>
);

/* ─── Project Status Row ─── */
const statusStyles = {
  Active: "bg-emerald-50 text-emerald-600 border border-emerald-200",
  "In Review": "bg-violet-50 text-violet-600 border border-violet-200",
  Blocked: "bg-red-50 text-red-600 border border-red-200",
  Completed: "bg-sky-50 text-sky-600 border border-sky-200",
};

const ProjectRow = ({ color, name, status, pct, team, due, dueWarn }) => (
  <tr className="hover:bg-gray-50/50" data-testid={`project-row-${name.toLowerCase().replace(/\s+/g, '-')}`}>
    <td className="py-2.5 px-4">
      <div className="flex items-center gap-2.5">
        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
        <span className="text-sm font-medium text-foreground">{name}</span>
      </div>
    </td>
    <td className="py-2.5 px-3"><span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full ${statusStyles[status] || "bg-gray-100 text-gray-500"}`}>{status}</span></td>
    <td className="py-2.5 px-3">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} /></div>
        <span className="text-[11px] font-semibold text-foreground/50 w-8">{pct}%</span>
      </div>
    </td>
    <td className="py-2.5 px-3">
      <div className="flex -space-x-1.5">
        {team?.map((t, i) => (
          <div key={i} className="w-6 h-6 rounded-full text-[9px] font-bold text-white flex items-center justify-center border-2 border-white" style={{ background: t.color }}>{t.initials}</div>
        ))}
      </div>
    </td>
    <td className={`py-2.5 px-3 text-xs ${dueWarn ? "text-red-500 font-medium" : "text-foreground/40"}`}>{due}</td>
  </tr>
);

/* ═══════════════════════════════════════════════════════ */
export const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardGoal, setWizardGoal] = useState("");
  const [wizardWorkflowHint, setWizardWorkflowHint] = useState("");
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const { isListening, startListening, stopListening } = useSpeechToText();

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening((text) => {
        setInput((prev) => prev ? prev + " " + text : text);
      });
    }
  }, [isListening, startListening, stopListening]);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachedFile(file);
      setInput((prev) => prev || `Analyze the attached file "${file.name}": `);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, []);

  /* Entrance animation */
  useEffect(() => { const t = setTimeout(() => setRevealed(true), 80); return () => clearTimeout(t); }, []);

  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/dashboard`, { withCredentials: true }); setData(r.data && typeof r.data === 'object' ? r.data : null); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  /* Close menu on outside click */
  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleSubmit = useCallback(async (text, workflowHint) => {
    const goal = text || input.trim();
    if (!goal || submitting) return;
    setWizardGoal(goal);
    // Auto-detect workflow type from goal
    const goalLower = goal.toLowerCase();
    let hint = workflowHint || "";
    if (!hint) {
      if (goalLower.includes("assessment") || goalLower.includes("quiz") || goalLower.includes("test") || goalLower.includes("exam")) hint = "assessment";
      else if (goalLower.includes("accessibility") || goalLower.includes("wcag") || goalLower.includes("a11y") || goalLower.includes("compliance")) hint = "accessibility";
      else if (goalLower.includes("locali") || goalLower.includes("translat") || goalLower.includes("language")) hint = "localization";
      else if (goalLower.includes("curriculum") || goalLower.includes("course") || goalLower.includes("study guide")) hint = "curriculum";
      else hint = "content";
    }
    setWizardWorkflowHint(hint);
    setWizardOpen(true);
    setInput("");
  }, [input, submitting]);

  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); } };

  const pickMenu = (item) => {
    setInput(item.prompt);
    setMenuOpen(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const firstName = user?.name?.split(" ")[0] || "there";

  return (
    <div className={`flex flex-col transition-opacity duration-700 ${revealed ? "opacity-100" : "opacity-0"}`} data-testid="dashboard-page">

      {/* ═══════════════ HERO — Conversational UI with teal/green gradient ═══════════════ */}
      <div className="relative overflow-visible" style={{ animation: revealed ? "heroReveal 0.8s ease-out" : "none" }}>
        {/* Gradient background — pipeline.html blue→teal→green */}
        <div className="absolute inset-0 overflow-hidden" style={{ background: "linear-gradient(160deg, rgb(23 111 163) 0%, rgb(40 156 209) 18%, rgb(58, 143, 170) 32%, rgb(45, 125, 149) 46%, rgb(37, 107, 122) 58%, rgb(30, 138, 110) 72%, rgb(35 171 113) 85%, rgb(8 103 59) 100%)" }}>
          {/* Animated orbs for depth */}
          <div className="absolute w-[520px] h-[520px] rounded-full -top-[120px] -right-[100px]" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.22) 0%, rgba(180,230,255,0.10) 45%, transparent 70%)", animation: "orbFloat 12s ease-in-out infinite" }} />
          <div className="absolute w-[400px] h-[400px] rounded-full -bottom-[80px] -left-[60px]" style={{ background: "radial-gradient(circle, rgba(150,255,200,0.18) 0%, rgba(80,220,160,0.08) 50%, transparent 70%)", animation: "orbFloat 15s ease-in-out infinite 3s" }} />
          <div className="absolute w-[280px] h-[280px] rounded-full top-[40%] right-[10%]" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 65%)", animation: "orbFloat 9s ease-in-out infinite 1.5s" }} />
          {/* Concentric arc rings */}
          <div className="absolute w-[700px] h-[700px] rounded-full border border-white/[0.1] -bottom-[200px] -left-[200px]" />
          <div className="absolute w-[500px] h-[500px] rounded-full border border-white/[0.1] -bottom-[100px] -left-[100px]" />
          <div className="absolute w-[300px] h-[300px] rounded-full border border-white/[0.1] bottom-0 left-0" />
        </div>

        <div className="relative z-10 flex flex-col items-center justify-center px-6 py-16 lg:py-24 min-h-[52vh]">

          {/* Greeting with gradient text */}
          <h1
            className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-[44px] tracking-tight text-white text-center mb-3 leading-[1.1]"
            style={{ animation: "cardFadeIn 0.5s ease-out 0.2s both" }}
            data-testid="dashboard-greeting"
          >
            What would you like to{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-white/90 to-white/60">create today?</span>
          </h1>
          <p className="text-sm text-white/50 text-center mb-10 max-w-md" style={{ animation: "cardFadeIn 0.5s ease-out 0.3s both" }}>
            Tell us about your learning goal, and Kadal AI will help you reach it.
          </p>

          {/* Suggested Action Pills + 3-dot menu */}
          <div className="flex items-center gap-2 mb-6 flex-wrap justify-center relative z-[30]" style={{ animation: "cardFadeIn 0.5s ease-out 0.35s both" }}>
            {[
              { icon: BookOpen, label: "Create a course", prompt: "Help me create a course on ", hint: "curriculum" },
              { icon: FlaskConical, label: "Build an assessment", prompt: "Build an assessment for ", hint: "assessment" },
              { icon: GraduationCap, label: "Design a study guide", prompt: "Design a study guide for ", hint: "curriculum" },
              { icon: Lightbulb, label: "Transform content", prompt: "Transform this content into a structured learning module: ", hint: "content" },
            ]?.map((action) => (
              <button
                key={action.label}
                onClick={() => { setInput(action.prompt); setTimeout(() => inputRef.current?.focus(), 100); }}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/[0.10] backdrop-blur-sm text-sm text-white font-medium hover:bg-black/[0.18] border-0"
                data-testid={`suggested-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <action.icon className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
                {action.label}
              </button>
            ))}
            {/* 3-dot menu trigger */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((p) => !p)}
                className={`flex items-center justify-center px-3 py-2 rounded-full backdrop-blur-sm text-sm font-medium border-0 ${
                  menuOpen ? "bg-black/[0.18] text-white" : "bg-black/[0.10] text-white hover:bg-black/[0.18]"
                }`}
                data-testid="more-options-btn"
                title="More options"
              >
                <MoreHorizontal className="w-4 h-4 text-white" strokeWidth={1.5} />
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-elevated border border-border/60 overflow-hidden z-[60]"
                  style={{ animation: "cardFadeIn 0.15s ease-out" }}
                  data-testid="more-options-menu"
                >
                  <div className="px-4 py-2.5 border-b border-border/40">
                    <p className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider">More Actions</p>
                  </div>
                  <div className="py-1.5">
                    {MENU_ITEMS?.map((item) => (
                      <button
                        key={item.label}
                        onClick={() => pickMenu(item)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-foreground/60 hover:text-foreground hover:bg-foreground/[0.02]"
                        data-testid={`menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.7} />
                        <span className="text-xs font-medium flex-1">{item.label}</span>
                        <ChevronRight className="w-3 h-3 opacity-25" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Input */}
          <div className="w-full max-w-xl" style={{ animation: "cardFadeIn 0.5s ease-out 0.4s both" }}>
            <div className="relative group">
              {/* Animated placeholder ticker (visible only when input is empty) */}
              {!input && (
                <div className="absolute left-5 top-1/2 -translate-y-1/2 pointer-events-none z-[1] overflow-hidden h-[22px]">
                  <div className="animate-placeholder-ticker">
                    {[
                      "Create a 6-week AI ethics course for undergraduates...",
                      "Build a standards-aligned assessment for Biology 101...",
                      "Design a study guide for organic chemistry...",
                      "Run an accessibility audit on our K-12 content library...",
                      "Transform this PDF into an interactive SCORM module...",
                      "Create a 6-week AI ethics course for undergraduates...",
                    ]?.map((text, i) => (
                      <div key={i} className="h-[22px] flex items-center text-[15px] text-foreground/45 leading-none whitespace-nowrap">{text}</div>
                    ))}
                  </div>
                </div>
              )}
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                className="w-full pl-5 pr-28 py-4 text-[15px] rounded-2xl border-2 border-transparent bg-white/95 backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.10)] resize-none outline-none focus:border-[#014d7d]/40 focus:shadow-[0_8px_40px_rgba(0,0,0,0.15)] leading-relaxed text-foreground scrollbar-hidden"
                style={{ minHeight: "76px", maxHeight: "160px" }}
                onInput={(e) => { e.target.style.height = "76px"; e.target.style.height = Math.min(e.target.scrollHeight, 160) + "px"; }}
                data-testid="dashboard-input"
                disabled={submitting}
              />
              <div className="absolute right-3 bottom-3 flex items-center gap-1">
                {/* Hidden file input */}
                <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" data-testid="file-input-hidden" />
                {/* Attached file chip */}
                {attachedFile && (
                  <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-foreground/5 text-xs text-foreground/60 mr-1">
                    <Paperclip className="w-3 h-3" />
                    <span className="max-w-[80px] truncate">{attachedFile.name}</span>
                    <button onClick={() => setAttachedFile(null)} className="hover:text-foreground" data-testid="remove-file-btn"><X className="w-3 h-3" /></button>
                  </span>
                )}
                {/* Attachment */}
                <button onClick={() => fileInputRef.current?.click()} className="w-8 h-8 rounded-xl text-foreground/45 hover:text-foreground/70 hover:bg-foreground/5 flex items-center justify-center" data-testid="input-attach-btn" title="Attach file">
                  <Paperclip className="w-4 h-4" />
                </button>
                {/* Audio */}
                <button
                  onClick={toggleListening}
                  className={`w-8 h-8 rounded-xl flex items-center justify-center ${isListening ? "bg-red-50 text-red-500" : "text-foreground/45 hover:text-foreground/70 hover:bg-foreground/5"}`}
                  data-testid="input-audio-btn"
                  title={isListening ? "Stop recording" : "Voice input"}
                >
                  {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
                {/* Send */}
                <button
                  onClick={() => handleSubmit()}
                  disabled={!input.trim() || submitting}
                  className="w-8 h-8 rounded-xl bg-[#014d7d] text-white flex items-center justify-center disabled:opacity-20 hover:bg-[#013d66] active:scale-95"
                  data-testid="dashboard-send-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightIcon className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════ RECENT CONVERSATIONS ═══════════════ */}
      {!loading && (data?.recent_conversations || []).length > 0 && (
        <div className="px-6 lg:px-8 pt-6 pb-2 bg-[#f8f9fb]" style={{ animation: revealed ? "cardFadeIn 0.6s ease-out 0.45s both" : "none" }}>
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading font-semibold text-sm text-foreground mb-3">Pick up where you left off</h2>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1" data-testid="recent-conversations">
              {(data.recent_conversations || [])?.map((conv) => (
                <Link
                  key={conv.id}
                  to={`/projects/${conv.project_id}`}
                  className="shrink-0 w-64 bg-white border border-border/60 rounded-xl p-4 hover:shadow-soft hover:border-border"
                  data-testid={`conv-${conv.id}`}
                >
                  <div className="flex items-start gap-2.5 mb-2">
                    <div className="w-7 h-7 rounded-lg bg-[#014d7d]/8 flex items-center justify-center shrink-0 mt-0.5">
                      <MessageSquare className="w-3.5 h-3.5 text-[#014d7d]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-foreground truncate">{conv.title}</p>
                      <p className="text-[10px] text-foreground/50 truncate mt-0.5">{conv.project_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-foreground/50">
                    <span>{conv.message_count} messages</span>
                    <span>&middot;</span>
                    <span>{new Date(conv.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════ BELOW THE FOLD — Data Dashboard ═══════════════ */}
      <div className="px-6 lg:px-8 py-10 bg-[#f8f9fb]" style={{ animation: revealed ? "cardFadeIn 0.6s ease-out 0.5s both" : "none" }}>
        <div className="max-w-6xl mx-auto space-y-6">

          {/* ── Recent Projects + Activity (MOVED TO TOP) ── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-border/60 rounded-2xl shadow-soft overflow-hidden" data-testid="recent-projects">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/40">
                <h2 className="font-heading font-semibold text-sm text-foreground">Recent Projects</h2>
                <Link to="/projects" className="text-xs text-cobalt font-medium hover:underline flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></Link>
              </div>
              {loading ? (
                <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-foreground/45 mx-auto" /></div>
              ) : (data?.recent_projects || []).length === 0 ? (
                <div className="p-10 text-center">
                  <FolderKanban className="w-8 h-8 text-foreground/10 mx-auto mb-3" />
                  <p className="text-sm text-foreground/40 mb-3">No projects yet</p>
                  <Link to="/projects" className="text-sm text-cobalt font-medium hover:underline flex items-center gap-1 justify-center" data-testid="create-first-project"><Plus className="w-3.5 h-3.5" /> Create your first project</Link>
                </div>
              ) : (
                <div className="divide-y divide-border/40">
                  {(data?.recent_projects || [])?.map((p) => (
                    <Link key={p.id} to={`/projects/${p.id}`} className="flex items-center justify-between px-5 py-3.5 hover:bg-gray-50/50" data-testid={`project-link-${p.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{p.name}</p>
                        <p className="text-xs text-foreground/40 truncate mt-0.5">{p.description || "No description"}</p>
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-0.5 rounded-full ${p.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-foreground/50"}`}>{p.status}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white border border-border/60 rounded-2xl shadow-soft overflow-hidden" data-testid="recent-activity">
              <div className="px-5 py-3.5 border-b border-border/40">
                <h2 className="font-heading font-semibold text-sm text-foreground">Recent Activity</h2>
              </div>
              {loading ? (
                <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-foreground/45 mx-auto" /></div>
              ) : (data?.recent_activities || []).length === 0 ? (
                <div className="p-10 text-center">
                  <Clock className="w-8 h-8 text-foreground/10 mx-auto mb-3" />
                  <p className="text-sm text-foreground/40">No activity yet</p>
                </div>
              ) : (
                <div className="divide-y divide-border/40 max-h-72 overflow-y-auto">
                  {(data?.recent_activities || [])?.map((a) => (
                    <div key={a.id} className="px-5 py-3">
                      <div className="flex items-start gap-2.5">
                        <div className="w-5 h-5 rounded-full bg-cobalt/8 flex items-center justify-center shrink-0 mt-0.5"><BarChart3 className="w-2.5 h-2.5 text-cobalt" /></div>
                        <div className="min-w-0">
                          <p className="text-xs text-foreground/70 leading-relaxed">{a.description}</p>
                          <p className="text-[10px] text-foreground/50 mt-0.5">{new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Metric Cards ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="metrics-grid">
            <MetricCard icon={FolderKanban} label="Total Assets" value="4,812" trend="+342" trendColor="bg-emerald-50 text-emerald-600" color="bg-sky-50 text-sky-600" />
            <MetricCard icon={Shield} label="Accessibility Score" value="69%" trend="-31%" trendColor="bg-amber-50 text-amber-600" color="bg-amber-50 text-amber-600" />
            <MetricCard icon={CheckCircle2} label="Awaiting Review" value="8" trend="pending" trendColor="bg-amber-50 text-amber-600" color="bg-rose-50 text-rose-500" />
            <MetricCard icon={TrendingUp} label="Derivatives Created" value="1,247" trend="+89" trendColor="bg-emerald-50 text-emerald-600" color="bg-emerald-50 text-emerald-600" />
          </div>

          {/* ── Content Health + Asset Breakdown ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-border/60 rounded-2xl shadow-soft overflow-hidden" data-testid="content-health">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/40">
                <h2 className="font-heading font-semibold text-sm text-foreground">Content Health</h2>
                <button className="text-xs text-foreground/40 hover:text-foreground font-medium">View Audit &rarr;</button>
              </div>
              <div className="px-5 py-4 space-y-1">
                <ProgressRow label="Accessibility (WCAG)" pct={69} color="#f59e0b" />
                <ProgressRow label="Metadata Completeness" pct={54} color="#ef4444" />
                <ProgressRow label="Assessment Coverage" pct={41} color="#ef4444" />
                <ProgressRow label="Standards Alignment" pct={78} color="#22c55e" />
                <ProgressRow label="Media Enrichment" pct={33} color="#3b82f6" />
              </div>
            </div>
            <div className="bg-white border border-border/60 rounded-2xl shadow-soft overflow-hidden" data-testid="asset-breakdown">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/40">
                <div><h2 className="font-heading font-semibold text-sm text-foreground">Asset Breakdown</h2><p className="text-[11px] text-foreground/40 mt-0.5">47 titles &middot; 4,812 assets</p></div>
              </div>
              <div className="px-5 py-4 flex items-center gap-6">
                <div className="relative shrink-0">
                  <svg width="80" height="80" viewBox="0 0 80 80">
                    <circle cx="40" cy="40" r="30" fill="none" stroke="#f1f5f9" strokeWidth="8" />
                    <circle cx="40" cy="40" r="30" fill="none" stroke="#3b82f6" strokeWidth="8" strokeDasharray="65 189" strokeLinecap="round" />
                    <circle cx="40" cy="40" r="30" fill="none" stroke="#1e40af" strokeWidth="8" strokeDasharray="40 189" strokeDashoffset="-65" strokeLinecap="round" />
                    <circle cx="40" cy="40" r="30" fill="none" stroke="#6366f1" strokeWidth="8" strokeDasharray="28 189" strokeDashoffset="-105" strokeLinecap="round" />
                    <circle cx="40" cy="40" r="30" fill="none" stroke="#22c55e" strokeWidth="8" strokeDasharray="22 189" strokeDashoffset="-133" strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center"><span className="text-sm font-heading font-bold text-foreground">47</span></div>
                </div>
                <div className="flex-1 space-y-2">
                  {[
                    { color: "#3b82f6", label: "Text / Documents", val: "2,410" },
                    { color: "#1e40af", label: "Media (A/V)", val: "847" },
                    { color: "#6366f1", label: "Assessments", val: "623" },
                    { color: "#22c55e", label: "Interactive", val: "492" },
                    { color: "#94a3b8", label: "Images", val: "440" },
                  ]?.map((item) => (
                    <div key={item.label} className="flex items-center gap-2 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: item.color }} />
                      <span className="text-foreground/60 flex-1">{item.label}</span>
                      <span className="font-semibold text-foreground/80">{item.val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ── Active Pipelines + Pipeline History ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-border/60 rounded-2xl shadow-soft overflow-hidden" data-testid="active-pipelines">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/40">
                <h2 className="font-heading font-semibold text-sm text-foreground">Active Pipelines</h2>
                <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">2 running</span>
              </div>
              <div>
                <PipelineRow name="K-12 Enrichment Pack" badge="running" badgeColor="bg-emerald-50 text-emerald-600 border border-emerald-200" pct={67} agents={5} />
                <PipelineRow name="Metadata Tagging - Q4 Titles" badge="running" badgeColor="bg-emerald-50 text-emerald-600 border border-emerald-200" pct={34} agents={4} />
                <PipelineRow name="Assessment Generation - Biology" badge="queued" badgeColor="bg-amber-50 text-amber-600 border border-amber-200" pct={0} agents={8} />
                <PipelineRow name="TTS Audio - Workforce Modules" badge="queued" badgeColor="bg-amber-50 text-amber-600 border border-amber-200" pct={0} agents={2} />
                <PipelineRow name="Accessibility Remediation" badge="completed" badgeColor="bg-cobalt/8 text-cobalt border border-cobalt/20" pct={100} agents={3} />
              </div>
            </div>
            <div className="bg-white border border-border/60 rounded-2xl shadow-soft overflow-hidden" data-testid="pipeline-history">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/40">
                <h2 className="font-heading font-semibold text-sm text-foreground">Pipeline History</h2>
                <button className="text-xs text-foreground/40 hover:text-foreground font-medium">View All &rarr;</button>
              </div>
              <div>
                <ActivityRow dot="#22c55e" text="<strong>Google Drive Sync</strong> &middot; STEM Collection" time="Ingest &middot; 4 hours ago" />
                <ActivityRow dot="#22c55e" text="<strong>Assessment Generator</strong> &middot; Biology 101" time="Transform &middot; 2 hours ago" />
                <ActivityRow dot="#3b82f6" text="<strong>Mastery Path</strong> &middot; Python for Beginners" time="Publish &middot; Yesterday" />
                <ActivityRow dot="#22c55e" text="<strong>Accessibility Remediation</strong> &middot; K-12" time="Transform &middot; 2 days ago" />
                <ActivityRow dot="#3b82f6" text="<strong>Glossary Extractor</strong> &middot; Full Library" time="Transform &middot; 3 days ago" />
              </div>
            </div>
          </div>

          {/* ── Project Status Table ── */}
          <div className="bg-white border border-border/60 rounded-2xl shadow-soft overflow-hidden" data-testid="project-status">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/40">
              <h2 className="font-heading font-semibold text-sm text-foreground">Project Status</h2>
              <Link to="/projects" className="text-xs text-foreground/40 hover:text-foreground font-medium flex items-center gap-1">View All <ArrowRight className="w-3 h-3" /></Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="project-status-table">
                <thead><tr className="border-b border-border/40">{["Project", "Status", "Progress", "Team", "Due"]?.map((h) => (<th key={h} className="text-left text-[10px] font-bold text-foreground/40 uppercase tracking-wider px-4 py-2.5">{h}</th>))}</tr></thead>
                <tbody>
                  <ProjectRow color="#1a6fc4" name="Higher Ed - Q2 2026" status="Active" pct={67} team={[{initials:"JP",color:"#1a6fc4"},{initials:"SA",color:"#1e8a4a"},{initials:"MR",color:"#c8841a"}]} due="Apr 30" />
                  <ProjectRow color="#6941c6" name="Biology Pilot" status="In Review" pct={84} team={[{initials:"JP",color:"#6941c6"},{initials:"LK",color:"#b84040"}]} due="Mar 31" />
                  <ProjectRow color="#1e8a4a" name="STEM Initiative" status="Active" pct={42} team={[{initials:"JP",color:"#1e8a4a"},{initials:"DM",color:"#1a6fc4"},{initials:"SA",color:"#c8841a"},{initials:"RN",color:"#6941c6"}]} due="Jun 15" />
                  <ProjectRow color="#c8841a" name="Workforce Compliance" status="Blocked" pct={28} team={[{initials:"MR",color:"#c8841a"},{initials:"JP",color:"#1a6fc4"}]} due="Mar 28" dueWarn />
                  <ProjectRow color="#0e7490" name="K-12 Content Suite" status="Completed" pct={100} team={[{initials:"LK",color:"#0e7490"},{initials:"DM",color:"#1e8a4a"}]} due="Feb 28" />
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Before vs After ── */}
          <div className="bg-white border border-border/60 rounded-2xl shadow-soft overflow-hidden" data-testid="before-after">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/40">
              <h2 className="font-heading font-semibold text-sm text-foreground">Before vs. After &middot; Last Enrichment Run</h2>
              <button className="text-xs text-foreground/40 hover:text-foreground font-medium">Full Report &rarr;</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 divide-x divide-border/40">
              {[
                { label: "Assessments", val: "+342", sub: "623 → 965", color: "#22c55e" },
                { label: "Metadata Tags", val: "+891", sub: "1,204 → 2,095", color: "#22c55e" },
                { label: "A11y Fixed", val: "+22%", sub: "47% → 69%", color: "#3b82f6" },
                { label: "Glossary Terms", val: "+1,420", sub: "0 → 1,420", color: "#22c55e" },
                { label: "TTS Audio", val: "+38", sub: "12 → 50 files", color: "#22c55e" },
              ]?.map((stat) => (
                <div key={stat.label} className="text-center py-5 px-3">
                  <p className="text-[9px] font-semibold uppercase tracking-wider text-foreground/40 mb-2">{stat.label}</p>
                  <p className="text-2xl font-heading font-bold" style={{ color: stat.color }}>{stat.val}</p>
                  <p className="text-[11px] text-foreground/40 mt-1">{stat.sub}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* Project Creation Wizard */}
      <CreateProjectWizard
        isOpen={wizardOpen}
        onClose={() => { setWizardOpen(false); setWizardGoal(""); setWizardWorkflowHint(""); }}
        prefillGoal={wizardGoal}
        prefillWorkflowType={wizardWorkflowHint}
      />
    </div>
  );
};
